import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config';
import { theme, alpha } from '../theme';
import { useResponsive } from '../hooks/useResponsive';

interface AuthGateProps {
  children: React.ReactNode;
  redirectTo?: string;
  onGuestAccess?: () => void;
}

const AuthGate: React.FC<AuthGateProps> = ({ children, redirectTo, onGuestAccess }) => {
  const { user, loading, loginWithGoogle, setToken, setUser } = useAuth();
  const navigate = useNavigate();
  const { id: matchId } = useParams();
  const [isJoiningAsGuest, setIsJoiningAsGuest] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const { isMobile } = useResponsive();

  // Si está cargando, mostrar loading
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        background: theme.colors.background,
        color: theme.colors.text
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>⚽</div>
          <div>Cargando...</div>
        </div>
      </div>
    );
  }

  // Si el usuario está autenticado, mostrar el contenido normal
  if (user) {
    return <>{children}</>;
  }

  // Si no está autenticado pero tiene guestAccess, permitir acceso
  const hasGuestAccess = localStorage.getItem('guestAccess') === 'true';
  if (hasGuestAccess) {
    return <>{children}</>;
  }

  // Si no está autenticado, mostrar opciones de registro
  const handleGoogleLogin = () => {
    // Guardar la URL de destino para redirigir después del login
    if (matchId) {
      localStorage.setItem('redirectAfterAuth', `/match/${matchId}`);
      // Guardar también el matchId para join automático
      localStorage.setItem('joinMatchAfterLogin', JSON.stringify({ matchId }));
    } else if (redirectTo) {
      localStorage.setItem('redirectAfterAuth', redirectTo);
    }
    loginWithGoogle();
  };

  const handleJoinAsGuest = async () => {
    setIsJoiningAsGuest(true);
    
    try {
      // Marcar que el usuario accedió como invitado
      localStorage.setItem('guestAccess', 'true');
      
      // Redirigir directamente al partido como invitado
      if (matchId) {
        navigate(`/match/${matchId}`);
      } else if (redirectTo) {
        navigate(redirectTo);
      }
      
      // Llamar al callback si existe
      if (onGuestAccess) {
        onGuestAccess();
      }
    } catch (error) {
      setIsJoiningAsGuest(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError(null);
    try {
      const response = await fetch(getApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Error en autenticacion');
      localStorage.setItem('authToken', data.token);
      setToken(data.token);
      // Fetch user profile with the new token
      const meRes = await fetch(getApiUrl('/api/auth/me'), {
        headers: { Authorization: `Bearer ${data.token}` },
      });
      const meData = await meRes.json();
      if (meRes.ok) setUser(meData);
      // Stay on the current match page
      if (matchId) navigate(`/match/${matchId}`);
      else if (redirectTo) navigate(redirectTo);
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      color: theme.colors.text,
      padding: isMobile ? '12px 4px' : theme.spacing.lg,
      marginLeft: isMobile ? 0 : '90px'
    }}>
      <div style={{
        background: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        padding: isMobile ? '14px 6px' : theme.spacing.lg,
        boxShadow: theme.colors.shadow,
        border: `2px solid ${theme.colors.border}`,
        maxWidth: isMobile ? '98vw' : 500,
        width: '100%',
        textAlign: 'center',
        margin: isMobile ? '0 auto' : undefined
      }}>
        {/* Header */}
        <div style={{ marginBottom: isMobile ? '18px' : theme.spacing.lg }}>
          <div style={{ fontSize: isMobile ? '2.2rem' : '48px', marginBottom: isMobile ? '10px' : theme.spacing.md }}>⚽</div>
          <h1 style={{ 
            fontSize: isMobile ? '1.4rem' : theme.fonts.sizeTitle, 
            fontWeight: 700, 
            color: theme.colors.primary,
            margin: 0,
            marginBottom: isMobile ? '8px' : theme.spacing.sm
          }}>
            ¡Bienvenido al Partido!
          </h1>
          <p style={{ 
            fontSize: isMobile ? '1rem' : theme.fonts.sizeBody, 
            color: theme.colors.textSecondary,
            margin: 0
          }}>
            Para unirte al partido, necesitas una cuenta o puedes entrar como invitado
          </p>
        </div>

        {/* Opciones de autenticación */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '10px' : theme.spacing.md }}>
          {/* Botón de Google */}
          <button
            onClick={handleGoogleLogin}
            className="google-btn"
          >
            <div style={{ fontSize: '20px' }}>🔐</div>
            Continuar con Google
          </button>

          {/* Email/Password Login */}
          <button
            onClick={() => setShowEmailLogin(!showEmailLogin)}
            style={{
              background: 'transparent',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.button,
              padding: '10px',
              color: theme.colors.textSecondary,
              fontSize: theme.fonts.sizeSmall,
              cursor: 'pointer',
            }}
          >
            {showEmailLogin ? 'Ocultar' : 'Iniciar sesion con email'}
          </button>

          {showEmailLogin && (
            <form onSubmit={handleEmailLogin} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  padding: '10px 12px',
                  borderRadius: theme.borderRadius.button,
                  border: `1px solid ${theme.colors.border}`,
                  background: theme.colors.background,
                  color: theme.colors.text,
                  fontSize: theme.fonts.sizeSmall,
                }}
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  padding: '10px 12px',
                  borderRadius: theme.borderRadius.button,
                  border: `1px solid ${theme.colors.border}`,
                  background: theme.colors.background,
                  color: theme.colors.text,
                  fontSize: theme.fonts.sizeSmall,
                }}
              />
              {loginError && (
                <div style={{ color: '#F44336', fontSize: '12px' }}>{loginError}</div>
              )}
              <button
                type="submit"
                disabled={loginLoading}
                style={{
                  padding: '10px',
                  borderRadius: theme.borderRadius.button,
                  border: 'none',
                  background: theme.colors.primary,
                  color: 'white',
                  fontWeight: 600,
                  fontSize: theme.fonts.sizeSmall,
                  cursor: loginLoading ? 'not-allowed' : 'pointer',
                  opacity: loginLoading ? 0.7 : 1,
                }}
              >
                {loginLoading ? 'Entrando...' : 'Entrar'}
              </button>
            </form>
          )}

          {/* Separador */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing.md,
            margin: `${theme.spacing.md} 0`
          }}>
            <div style={{ flex: 1, height: '1px', background: theme.colors.border }}></div>
            <span style={{ color: theme.colors.textSecondary, fontSize: theme.fonts.sizeSmall }}>o</span>
            <div style={{ flex: 1, height: '1px', background: theme.colors.border }}></div>
          </div>

          {/* Botón de invitado */}
          <button
            onClick={() => {
              handleJoinAsGuest();
            }}
            disabled={isJoiningAsGuest}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing.sm,
              padding: theme.spacing.md,
              background: theme.colors.secondary,
              color: 'white',
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontSize: theme.fonts.sizeBody,
              fontWeight: 600,
              cursor: isJoiningAsGuest ? 'not-allowed' : 'pointer',
              opacity: isJoiningAsGuest ? 0.7 : 1,
              boxShadow: theme.colors.shadow,
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              if (!isJoiningAsGuest) {
                e.currentTarget.style.background = theme.colors.primary;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isJoiningAsGuest) {
                e.currentTarget.style.background = theme.colors.secondary;
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            <div style={{ fontSize: '20px' }}>👤</div>
            {isJoiningAsGuest ? 'Entrando...' : 'Entrar como Invitado'}
          </button>
        </div>

        {/* Información adicional */}
        <div style={{ 
          marginTop: isMobile ? '14px' : theme.spacing.lg,
          padding: isMobile ? '10px 6px' : theme.spacing.md,
          background: `linear-gradient(135deg, ${alpha('primary', '11')}, ${alpha('primary', '09')})`,
          borderRadius: theme.borderRadius.button,
          border: `1px solid ${alpha('primary', '33')}`
        }}>
          <h3 style={{ 
            fontSize: isMobile ? '1rem' : theme.fonts.sizeBody, 
            fontWeight: 600, 
            color: theme.colors.primary,
            margin: '0 0 8px 0'
          }}>
            💡 ¿Por qué registrarse?
          </h3>
          <ul style={{ 
            textAlign: 'left', 
            margin: 0, 
            paddingLeft: isMobile ? '18px' : theme.spacing.md,
            fontSize: isMobile ? '0.93rem' : theme.fonts.sizeSmall,
            color: theme.colors.textSecondary
          }}>
            <li>Acceso a todos tus partidos</li>
            <li>Notificaciones en tiempo real</li>
            <li>Historial de partidos jugados</li>
            <li>Perfil personalizado</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AuthGate; 